import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BauenLogoFull } from "@/components/BauenLogo";
import { Loader2, Check, X } from "lucide-react";

function useStrength(password) {
  const rules = [
    { label: "Ao menos 8 caracteres", ok: password.length >= 8 },
    { label: "Uma letra maiúscula", ok: /[A-Z]/.test(password) },
    { label: "Uma letra minúscula", ok: /[a-z]/.test(password) },
    { label: "Um número", ok: /\d/.test(password) },
  ];
  const score = rules.filter((r) => r.ok).length;
  return { rules, score, ok: rules.every((r) => r.ok) };
}

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const strength = useStrength(password);

  const submit = async (e) => {
    e.preventDefault();
    if (!strength.ok) { toast.error("A senha ainda não atende aos critérios"); return; }
    if (password !== confirm) { toast.error("As senhas não conferem"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Senha criada com sucesso! Faça login para continuar.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível criar a senha. O convite pode ter expirado.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="set-password-page">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/9458996/pexels-photo-9458996.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940"
          alt="Arquitetura moderna BAUEN"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2E3B]/95 via-[#0A2E3B]/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end h-full p-12 text-white">
          <div className="max-w-md">
            <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight">
              Bem-vindo(a) à BAUEN.
            </h1>
            <p className="mt-4 text-white/80 text-sm leading-relaxed">
              Para acessar o portal e acompanhar seus projetos, crie agora uma senha
              segura de sua escolha. Este link é único e válido por 7 dias.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md animate-fade-in-up">
          <BauenLogoFull className="mb-8" />
          <h2 className="font-heading text-2xl font-semibold">Crie sua senha de acesso</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Defina uma senha forte para acessar o Portal BAUEN.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="sp-pw">Nova senha</Label>
              <Input id="sp-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="set-password-input" placeholder="••••••••" autoFocus />
              {password.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="password-strength-rules">
                  {strength.rules.map((r) => (
                    <li key={r.label} className={`flex items-center gap-2 text-xs ${r.ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                      {r.ok ? <Check size={12} /> : <X size={12} className="text-muted-foreground/60" />}
                      {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sp-confirm">Confirmar senha</Label>
              <Input id="sp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="set-password-confirm-input" placeholder="••••••••" />
              {confirm.length > 0 && confirm !== password && (
                <p className="text-xs text-brand-red">As senhas não coincidem</p>
              )}
            </div>
            <Button type="submit" disabled={loading || !token || !strength.ok || password !== confirm} className="w-full h-11" data-testid="set-password-submit-btn">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Criando…</> : "Criar senha e acessar"}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            Já tem uma senha?{" "}
            <Link to="/login" className="text-primary hover:underline">Fazer login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
